import { useCallback, useState } from 'react'
import {
  Box,
  Button,
  HStack,
  IconButton,
  Text,
  VStack,
  Badge,
} from '@chakra-ui/react'
import { Check, Circle } from 'lucide-react'
import { useThemeColors } from '@/hooks/useThemeColors'
import { childTicketService, type ChildTicketTask } from '@/lib/services/childTicketService'
import { useToastNotification } from '@/lib/utils/useToastNotification'
import type { TicketPart } from '@/types/parentTicket'
import TaskRowActions from './TaskRowActions'
import TaskPanelBadge from './TaskPanelBadge'
import TaskStatusPill from './TaskStatusPill'
import TaskSlaCard from './TaskSlaCard'
import { resolveTaskUiType } from './taskTypeBadge'
import {
  isTaskBlocked,
  isTaskBreached,
  isTaskComplete,
  isTaskSkipped,
  isFeVisitSuperseded,
  computeTaskAgingHours,
  getTaskAgingBucket,
  formatTaskAgeHours,
  getPartsProcurementCompletion,
  TASK_AGING_BUCKET_COLORS,
  TASK_AGING_BUCKET_LABELS,
} from './taskUtils'
import { getSlaStatus } from '@/lib/utils/slaStatus'

type Props = {
  parentTicketId: string
  tasks: ChildTicketTask[]
  /** Ticket-level parts list (fetched once upstream) for parts_procurement complete gating. */
  ticketParts?: TicketPart[]
  onTasksChange?: () => void | Promise<void>
  ticketPropertiesProps?: Record<string, unknown>
  onEditTask?: (task: ChildTicketTask) => void
  onCreateTask?: () => void
}

export default function TaskListView({
  parentTicketId,
  tasks,
  ticketParts = [],
  onTasksChange,
  onEditTask,
  onCreateTask,
}: Props) {
  const colors = useThemeColors()
  const { showError, showSuccess } = useToastNotification()
  const [completingId, setCompletingId] = useState<number | null>(null)

  const refresh = useCallback(async () => {
    await onTasksChange?.()
  }, [onTasksChange])

  const openEdit = (task: ChildTicketTask) => {
    onEditTask?.(task)
  }

  const handleComplete = async (task: ChildTicketTask) => {
    if (isTaskComplete(task) || isTaskBlocked(task, tasks)) return

    const uiType = resolveTaskUiType(task)
    if (uiType === 'fe_visit') return
    if (uiType === 'parts_procurement') {
      const { partsLinked, partsComplete } = getPartsProcurementCompletion(task.id, ticketParts)
      if (partsLinked && !partsComplete) return
    }

    if (isTaskBreached(task)) {
      const ok = window.confirm('This task is overdue. Mark it complete anyway?')
      if (!ok) return
    }
    setCompletingId(task.id)
    try {
      await childTicketService.complete(parentTicketId, task.id, task.status ?? undefined)
      showSuccess('Task completed')
      await refresh()
    } catch (err) {
      showError(err as Error)
    } finally {
      setCompletingId(null)
    }
  }

  if (tasks.length === 0) {
    return (
      <Box py={8} textAlign="center">
        <Text color={colors.textSecondary} mb={3}>
          No tasks match this filter.
        </Text>
        <Button size="sm" colorScheme="blue" variant="outline" onClick={onCreateTask}>
          Create task
        </Button>
      </Box>
    )
  }

  return (
    <VStack align="stretch" spacing={2}>
      {tasks.map((task) => {
        const skipped = isTaskSkipped(task)
        const superseded = isFeVisitSuperseded(task)
        const done = isTaskComplete(task) && !skipped
        const blocked = isTaskBlocked(task, tasks)
        const completing = completingId === task.id
        const uiType = resolveTaskUiType(task)
        const partsCompletion =
          uiType === 'parts_procurement'
            ? getPartsProcurementCompletion(task.id, ticketParts)
            : null
        const blockFeVisitComplete = uiType === 'fe_visit' && !done && !skipped
        const blockPartsComplete =
          Boolean(partsCompletion?.partsLinked && !partsCompletion.partsComplete) && !done && !skipped
        const completeDisabled =
          done || skipped || blocked || completing || blockFeVisitComplete || blockPartsComplete

        let completeTitle: string | undefined
        if (blockFeVisitComplete) {
          completeTitle = 'Completed from the mobile app'
        } else if (blockPartsComplete && partsCompletion) {
          completeTitle = `${partsCompletion.unresolvedCount} part${partsCompletion.unresolvedCount === 1 ? '' : 's'} not yet received`
        } else if (blocked) {
          completeTitle = 'Blocked by dependency'
        } else if (skipped) {
          completeTitle = 'Skipped'
        } else if (done) {
          completeTitle = 'Completed'
        }

        const slaDetail = task.sla_due_at
          ? getSlaStatus(task.sla_due_at).detail ?? getSlaStatus(task.sla_due_at).label
          : null

        const agingHours = computeTaskAgingHours(task)
        const agingBucket = getTaskAgingBucket(task)

        return (
          <TaskSlaCard
            key={task.id}
            slaDueAt={task.sla_due_at}
            completedAt={task.actual_end}
            onClick={() => openEdit(task)}
            cursor="pointer"
            showDetail={false}
          >
            <HStack align="flex-start" spacing={3}>
              <IconButton
                aria-label={
                  blockFeVisitComplete
                    ? 'Completed from the mobile app'
                    : blockPartsComplete
                      ? completeTitle ?? 'Mark complete'
                      : skipped
                        ? 'Skipped'
                        : done
                          ? 'Completed'
                          : 'Mark complete'
                }
                title={completeTitle}
                icon={skipped ? <Text fontSize="xs" fontWeight="700">—</Text> : done ? <Check size={16} /> : <Circle size={16} />}
                size="sm"
                variant="ghost"
                colorScheme={skipped ? 'gray' : done ? 'green' : 'gray'}
                isDisabled={completeDisabled}
                isLoading={completing}
                onClick={(e) => {
                  e.stopPropagation()
                  void handleComplete(task)
                }}
                mt={0.5}
              />
              <Box flex="1" minW={0}>
                <HStack spacing={2} align="center" flexWrap="wrap">
                  <Text
                    fontSize="sm"
                    fontWeight="700"
                    color={skipped ? colors.textMuted : colors.text}
                    noOfLines={2}
                    textDecoration={skipped ? 'line-through' : undefined}
                  >
                    {task.title || task.notes || task.task_number}
                  </Text>
                  {skipped ? (
                    <Badge colorScheme="gray" fontSize="2xs">
                      Skipped
                    </Badge>
                  ) : null}
                  {superseded ? (
                    <TaskStatusPill status={task.status} statusLabel={task.status_label} />
                  ) : null}
                  {task.task_number ? (
                    <Text fontSize="xs" color={colors.textMuted}>
                      · {task.task_number}
                    </Text>
                  ) : null}
                  <TaskPanelBadge task={task} />
                </HStack>
                <Text fontSize="xs" color={colors.textSecondary} mt={0.5}>
                  {task.assignee_name ?? 'Unassigned'}
                  {slaDetail ? ` · ${slaDetail}` : ''}
                  {blocked ? ' · Blocked' : ''}
                </Text>
              </Box>
              {agingBucket ? (
                <VStack spacing={0} align="flex-end" flexShrink={0} minW="72px">
                  <Text fontSize="2xs" color={colors.textMuted} textTransform="uppercase" letterSpacing="wider">
                    Age
                  </Text>
                  <Badge
                    colorScheme={TASK_AGING_BUCKET_COLORS[agingBucket]}
                    fontSize="2xs"
                    title={agingHours !== null ? formatTaskAgeHours(agingHours) : undefined}
                  >
                    {TASK_AGING_BUCKET_LABELS[agingBucket]}
                  </Badge>
                  {task.age_in_status_category && task.age_in_status_hours != null ? (
                    <Text fontSize="2xs" color={colors.textMuted} mt={0.5} textAlign="right">
                      In {task.status_label ?? 'status'} {formatTaskAgeHours(task.age_in_status_hours)}
                    </Text>
                  ) : null}
                </VStack>
              ) : null}
              <Box onClick={(e) => e.stopPropagation()}>
                <TaskRowActions task={task} onEdit={() => openEdit(task)} />
              </Box>
            </HStack>
          </TaskSlaCard>
        )
      })}
    </VStack>
  )
}
